<template>
  <div class="v-article-page container" data-test-article-page>
    <AdnBreadcrumbs :breadcrumbs="breadcrumbs" class="v-article-page__breadcrumbs" v-if="shouldDisplayBreadcrumb" />

    <div v-if="article?.title" class="v-article-page__content">
      <article>
        <header>
          <section v-if="hubs?.length" :class="['hub-tags', { 'hub-tags--default-cursor': !shouldDisplayTagLinks }]">
            <div>
              <adn-tag v-for="hub of hubs" :key="hub.id">
                <template v-if="shouldDisplayTagLinks">
                  <div @click="onClick(hubUrl(hub), hub.title)">{{ hub.title }}</div>
                </template>
                <template v-else>
                  <div>{{ hub.title }}</div>
                </template>
              </adn-tag>
            </div>
          </section>

          <h1>{{ article.title }}</h1>

          <section class="article-infos">
            <div>
              <span aria-hidden="true" class="adn-icon adn-icon--function-calendar" />
              {{ formatDate(article.header.publishedAt, 'd LLL yyyy') }}
            </div>
            <div v-if="article.header.author">
              <span aria-hidden="true" class="adn-icon adn-icon--category-adherents" />
              {{ article.header.author }}
            </div>
          </section>

          <section class="image">
            <adn-image
              v-if="article.header.image || article.header.imageUrl"
              :alt="article.header.imageAlt"
              :max-width-to-autosize="950"
              :source="article.header.image || article.header.imageUrl"
            />
            <small v-if="article.header.imageLegend">{{ article.header.imageLegend }}</small>
          </section>
        </header>

        <CArticleContent :article="article" />

        <PArticleSocialButtons
          v-if="shouldDisplaySocialShare"
          :image-src="article.header.image || article.header.imageUrl"
          :title="article.title"
          align="right"
          data-test-share
          :label="i18n._('Partager la page')"
        />

        <template v-if="article.advertorialInserts">
          <p-advertorial-insert
            v-for="advertorial in article.advertorialInserts"
            :key="advertorial.id"
            :value="advertorial"
            @card-visible="onVisibilityChanged(advertorial)"
          />
        </template>

        <PNewsletterSubscription v-if="isAladin" form-target-id="articlesItemsNL" />
      </article>

      <aside v-if="shouldDisplaySocialShare || shouldDisplayNewsletter" class="article-page__advertorials">
        <PArticleSocialButtons
          v-if="shouldDisplaySocialShare"
          :image-src="article.header.image || article.header.imageUrl"
          :title="article.title"
          data-test-share
          :label="i18n._('Partager la page')"
        />
        <PNewsletterSubscription v-if="shouldDisplayNewsletter" />
        <div v-if="article.advertorialInserts" class="advertorialInserts">
          <p-advertorial-insert
            v-for="advertorial in article.advertorialInserts"
            :key="advertorial.id"
            :value="advertorial"
            @card-visible="onVisibilityChanged(advertorial)"
          />
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '@b2ag/l10n'
import { useRoute, useRouter } from 'vue-router'
import {
  computed,
  defineAsyncComponent,
  useAsyncData,
  type Ref,
  ref,
  useHead,
  useNuxtApp,
  createError,
  watch,
} from '#imports'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import AdnBreadcrumbs from '@b2ag/design-system/src/components/AdnBreadcrumbs/AdnBreadcrumbs.vue'
import AdnTag from '@b2ag/design-system/src/components/AdnTag/AdnTag.vue'
import type { BreadCrumb } from '@b2ag/search/src/types/BreadCrumb'
import { i18n } from '@b2ag/locale'
import { isAladin } from '@b2ag/app-brands'
import { useStore } from 'vuex'
import AdnImage from '@b2ag/design-system/src/components/AdnImage/AdnImage.vue'
import type { AdvertorialInsert } from '../../../domain/Advertorialnsert'
import type { Hub } from '../../../domain/Hub'
import { NotForPartnerError } from '../../../domain/NotForPartnerError'
import { NotForNationalError } from '../../../domain/NotForNationalError'
import { articleUseCase, iHubRepository } from '../../../dependency-injection'
import PNewsletterSubscription from '../../presenters/PNewsletterSubscription.vue'
import PAdvertorialInsert from '../../presenters/PAdvertorialInsert/PAdvertorialInsert.vue'
import { mapArticleMetaInfo } from '../../composable/article.meta-info.mappers'
import PArticleSocialButtons from '../../presenters/PArticleSocialButtons.vue'
import { generateLink } from '@b2ag/seo/src/helper/generateLink'

const CArticleContent = defineAsyncComponent(() => import('../../components/CArticleContent/CArticleContent.vue'))

// Définition des props
const { excludeComponents } = defineProps<{
  excludeComponents?: string[]
}>()

const nuxtApp = useNuxtApp()
const route = useRoute()
const router = useRouter()
const store = useStore()
const isAuthenticated = computed(() => {
  return store.getters.isAuthenticated
})
const partnerId = computed(() => {
  return store.getters.currentCooperativeId
})
const shouldDisplaySocialShare = computed(() => !excludeComponents?.includes('social-share'))
const shouldDisplayBreadcrumb = computed(() => !excludeComponents?.includes('breadcrumb'))
const shouldDisplayTagLinks = computed(() => !excludeComponents?.includes('tags-links'))
const shouldDisplayNewsletter = computed(() => !excludeComponents?.includes('newsletter') && isAladin)

const breadcrumbs: Ref<BreadCrumb[]> = ref([
  { name: i18n._('Accueil'), route: { name: 'home' } },
  { name: i18n._('Toutes les actualités agricoles'), route: { name: 'annuaire' } },
])

const hubUrl = (hub: Hub) => `${router?.resolve({ name: 'hub-page', params: { slug: hub.id } }).href}`

let advertorialInsertsVisible: Array<string> = []
let timeoutHandler: NodeJS.Timeout | undefined

const onClick = (link: string, title: string) => {
  useAnalyticService().trackEvent('click_tags_articles', {
    content_title: title,
  })
  router?.push(link)
}

function onVisibilityChanged(advertorial: AdvertorialInsert) {
  if (timeoutHandler) {
    clearTimeout(timeoutHandler)
  }
  advertorialInsertsVisible.push(advertorial.id)
  timeoutHandler = setTimeout(() => {
    useAnalyticService().trackEvent('view_advertorial_insert', {
      content_title: advertorialInsertsVisible,
    })
    advertorialInsertsVisible = []
  }, 500)
}

const {
  data: article,
  error: err,
  refresh: refreshArticle,
} = await useAsyncData(
  'article-entity-' + route.params.slug,
  async () => await articleUseCase.getArticle(route.params.slug as string, isAuthenticated.value, partnerId.value),
)

const { data: hubs, refresh: refreshHubs } = await useAsyncData(
  'article-hubs-' + route.params.slug,
  async () => await iHubRepository.getHubsByTags(article.value.tags),
)

const { data: articleLocales, refresh: refreshLocales } = await useAsyncData(
  'article-locale-' + route.params.slug,
  async () => await articleUseCase.getArticleLocales(article.value.id),
)

watch(
  () => article.value,
  async () => {
    if (!article.value) return

    breadcrumbs.value.push({ name: article.value.title } as BreadCrumb)

    if (article.value?.tags && !hubs.value) refreshHubs()

    if (!articleLocales.value) refreshLocales()
  },
  {
    immediate: true,
  },
)

watch(
  () => articleLocales.value,
  () => {
    if (!articleLocales.value) return

    const articleLocalesEntries = Object.entries(articleLocales.value)
    const links = articleLocalesEntries.map(([locale, slug]) => {
      const localesUrl = articleLocalesEntries.length > 1 ? locale.split('-').reverse().join('/') + '/' : ''
      const origin =
        typeof window !== 'undefined'
          ? window.location.origin
          : `${nuxtApp.ssrContext?.event.node.req.headers['x-forwarded-proto']}://${nuxtApp.ssrContext?.event.node.req.headers.host}`
      return generateLink('alternate', `${origin}/${localesUrl}article/${slug}`, locale)
    })
    useHead(() => mapArticleMetaInfo(article.value, route.fullPath, links))
  },
  {
    immediate: true,
  },
)

watch(
  () => err.value,
  () => {
    if (err.value) {
      if (err.value.message === new NotForNationalError().message) {
        if (typeof window !== 'undefined') {
          if (isAuthenticated.value) refreshArticle()
          else router?.push({ name: 'login' })
        }
      } else if (err.value.message === new NotForPartnerError().message) {
        if (typeof window !== 'undefined') {
          if (isAuthenticated.value) router?.push({ name: 'annuaire' })
          else router?.push({ name: 'login' })
        }
      } else
        throw createError({
          statusCode: 404,
          fatal: true,
        })
    }
  },
  {
    immediate: true,
  },
)
</script>

<style lang="scss" scoped>
.v-article-page {
  .v-article-page__breadcrumbs {
    margin-top: var(--spacing-5);
    margin-bottom: var(--spacing-2);
  }

  .v-article-page__content {
    background-color: var(--color-background-light);
    border-radius: var(--border-radius-2);
    display: flex;
    gap: var(--spacing-4);

    @media screen and (min-width: $breakpoint-md) {
      gap: 0;
      padding: var(--spacing-4) var(--spacing-2);
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: var(--spacing-4);
      padding: var(--spacing-4);
    }

    > article {
      display: flex;
      flex-direction: column;
      overflow: hidden;

      @media screen and (min-width: $breakpoint-md) {
        padding: 0;
        padding-right: var(--spacing-2);
      }

      @media screen and (min-width: $breakpoint-lg) {
        padding: var(--spacing-3);
      }

      width: 100%;

      header {
        .hub-tags {
          margin-bottom: var(--spacing-2);

          > :first-child {
            display: flex;
            gap: var(--spacing-3);
            flex-wrap: wrap;

            > * {
              text-transform: uppercase;
              white-space: nowrap;
              cursor: pointer;
            }
          }

          &--default-cursor {
            > :first-child {
              > * {
                cursor: default;
              }
            }
          }
        }

        .article-infos {
          color: var(--color-grey-dark);
          display: flex;
          gap: var(--spacing-4);
          margin-top: var(--spacing-2);

          > * {
            align-items: center;
            display: flex;
            gap: var(--spacing-2);
          }
        }

        .image {
          margin: var(--spacing-6) 0;

          small {
            margin-top: var(--spacing-2);
            color: var(--color-text-light);
            word-break: break-word;
          }

          img {
            display: block;
            object-fit: cover;
            object-position: center;
            width: 100%;
          }
        }
      }

      .social-network-sharing {
        display: flex;
        justify-content: flex-end;
      }

      .p-advertorial-insert {
        display: none;
      }

      .idf-newsletter-subscription__container {
        display: none;
      }

      .c-article-social-buttons--right {
        align-self: flex-end;
        margin-top: var(--spacing-3);
        margin-bottom: var(--spacing-3);
      }
    }

    > aside {
      @media screen and (min-width: $breakpoint-md) {
        flex: 0 1 28rem;
        padding: 0;

        .c-article-social-buttons {
          flex-wrap: wrap;

          > * {
            width: 100%;
          }
        }
      }

      @media screen and (min-width: $breakpoint-lg) {
        flex: 0 0 28rem;
        padding: 0;

        .c-article-social-buttons {
          flex-wrap: nowrap;
        }
      }

      .advertorialInserts {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-6);
        margin-top: var(--spacing-6);
      }

      .idf-newsletter-subscription__container {
        margin-top: var(--spacing-6);
      }
    }
  }

  .v-article-page__newsletter {
    margin: var(--spacing-5) 0;
  }
}

@media all and (max-width: ($breakpoint-md - 1)) {
  .v-article-page {
    margin: auto;
    margin-bottom: var(--spacing-6);
    padding: 0;

    .v-article-page__breadcrumbs {
      display: none;
    }

    .v-article-page__content {
      border-radius: initial;
      padding: var(--spacing-3);

      > article {
        header {
          .hub-tags {
            overflow: auto;

            > :first-child {
              flex-wrap: nowrap;
            }
          }

          .article-infos {
            flex-direction: column;
          }
        }

        .idf-newsletter-subscription__container {
          display: flex;
          margin-top: var(--spacing-4);
          margin-bottom: var(--spacing-4);

          :deep(img) {
            display: none;
          }
        }

        .p-advertorial-insert {
          max-height: 40rem;

          :deep(.p-advertorial-insert__image) {
            object-fit: cover;
            max-height: 40rem;
          }

          display: block;
          margin-bottom: var(--spacing-4);

          &:last-child {
            margin-top: var(--spacing-4);
          }
        }
      }

      > aside {
        display: none;
      }
    }
  }
}
</style>
